'use client';
import { Button } from "@/components/atoms/button";
//import Image from "next/legacy/image";
import { useEffect, useState } from "react";

import Link from "next/link";
import { motion, AnimatePresence } from "framer-motion";
import { IBanner } from "@/services/types";
import React from "react";
import { BrandChevron } from "@/components/organisms/HeroBanner/BrandChevron";
//import SimuladorChatBot from "../SimuladorChatBot";

interface HeroCarouselProps {
    slidesBanner: IBanner[];
}

export default function HeroCarousel({ slidesBanner }: HeroCarouselProps) {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [touchStart, setTouchStart] = useState(0);
    const [touchEnd, setTouchEnd] = useState(0);

    const totalSlides = slidesBanner.length;

    useEffect(() => {
        const timer = setTimeout(() => {
            setCurrentSlide((prev) => (prev + 1) % totalSlides);
        }, 9000);
        return () => clearTimeout(timer);
    }, [currentSlide, totalSlides]);

    // Funções para swipe no mobile
    const handleTouchStart = (e: React.TouchEvent) => {
        setTouchStart(e.targetTouches[0].clientX);
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const handleTouchEnd = () => {
        if (!touchStart || !touchEnd) return;

        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > 50;
        const isRightSwipe = distance < -50;

        if (isLeftSwipe) {
            setCurrentSlide((prev) => (prev + 1) % totalSlides);
        }
        if (isRightSwipe) {
            setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
        }

        setTouchStart(0);
        setTouchEnd(0);
    };

    if (!slidesBanner || slidesBanner.length === 0) return null;

    return (
        <>
            <div
                className="relative w-full flex items-end mt-20"
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
            >
                <div className="relative h-[500px] sm:h-[700px] md:h-[700px] lg:h-[800px] xl:h-[960px] w-full flex items-end">
                    <div className="w-full h-[500px] sm:h-[700px] md:h-[700px] lg:h-[800px] xl:h-[854px]">

                        {/* SVG Gradient Overlay */}
                        <svg
                            className="absolute bottom-0 left-0 right-0 w-full h-26 sm:h-64 md:h-72 lg:h-80 xl:h-96 pointer-events-none z-10"
                            viewBox="0 0 1920 490"
                            xmlns="http://www.w3.org/2000/svg"
                            preserveAspectRatio="none"
                            aria-hidden="true"
                        >
                            <defs>
                                <linearGradient id="paint0_linear_3757_31277" x1="305.421" y1="138.814" x2="1743.34" y2="900.336" gradientUnits="userSpaceOnUse">
                                    <stop offset="0.0257575" stopColor="#EF4130" stopOpacity="0.8" />
                                    <stop offset="0.254366" stopColor="#F15A29" stopOpacity="0.7" />
                                    <stop offset="0.367337" stopColor="#F6881F" stopOpacity="0.5" />
                                    <stop offset="0.586538" stopColor="#F68D1E" stopOpacity="0.4" />
                                    <stop offset="0.764423" stopColor="#F7941D" stopOpacity="0.5" />
                                    <stop offset="1" stopColor="#F15A29" stopOpacity="0.7" />
                                </linearGradient>
                            </defs>
                            {/* Triângulo branco no canto inferior esquerdo */}
                            <path
                                d="M0 253V490H199.477V489.782L0 253Z"
                                fill="#fff" fillOpacity=""
                            />
                            {/* Path com gradiente laranja como overlay */}
                            <path
                                d="M1920 0V489.955H199.661V490.002L199.622 489.955H199.477V489.782L0 253V0.0488281L199.477 0.046875V0H1920Z"
                                fill="transparent"
                            />
                        </svg>
                        {/* BG com fade */}
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={`bg-${currentSlide}`}
                                initial={{ opacity: .8, scale: 1 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 1 }}
                                transition={{ duration: .4, ease: "easeInOut" }}
                                className="absolute inset-0 h-full w-full bg-cover bg-[position:68%_center] bg-no-repeat z-0 quickly sm:bg-center"
                                style={{
                                    backgroundImage: `url(${slidesBanner[currentSlide].image?.url}) `,
                                }}
                            />
                        </AnimatePresence>

                        <div
                            className="absolute inset-0 z-10 pointer-events-none bg-[linear-gradient(180deg,rgba(0,0,0,0.74)_0%,rgba(0,0,0,0.58)_42%,rgba(0,0,0,0.34)_70%,rgba(0,0,0,0.12)_100%)] lg:bg-none"
                            aria-hidden="true"
                        />

                        {/* Conteúdo com slide */}
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={`content-${currentSlide}`}
                                initial={{ x: "100%", opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                exit={{ x: "-100%", opacity: 0 }}
                                transition={{ duration: 0.5, ease: "easeInOut" }}
                                className="absolute inset-0 z-20 flex flex-col lg:flex-row justify-start lg:justify-center items-center"
                            >
                                <div className="container flex flex-col lg:flex-row justify-between h-full px-5 sm:px-6 md:px-8 lg:px-12 xl:px-16">
                                    {/* Texto */}
                                    <div className="h-full flex flex-col justify-start lg:justify-center items-center lg:items-start gap-5 sm:gap-5 md:gap-5 lg:gap-6 w-full lg:w-[500px] xl:w-[600px] text-center lg:text-left pt-24 sm:pt-32 md:pt-32 lg:py-0 pb-4">
                                        <h1 className="text-white lg:text-[#2B2F31] uppercase font-bold text-[1.6rem] sm:text-3xl md:text-3xl lg:text-4xl xl:text-5xl leading-[1.12] max-w-[21rem] sm:max-w-md lg:max-w-[480px] xl:max-w-[560px]">
                                            {(() => {
                                                const title = slidesBanner[currentSlide].title;
                                                const highlightPhrase = slidesBanner[currentSlide].highlight_title || "";

                                                if (!highlightPhrase || !title.includes(highlightPhrase)) {
                                                    return title;
                                                }

                                                const parts = title.split(highlightPhrase);

                                                // Definir cor baseada no índice do slide
                                                const getHighlightColor = (slideIndex: number) => {
                                                    switch (slideIndex) {
                                                        case 0: return 'text-[#F15A29]';
                                                        case 1: return 'text-[#F15A29]';
                                                        case 2: return 'text-[#F15A29]';
                                                        default: return 'text-[#F15A29]';
                                                    }
                                                };

                                                return (
                                                    <>
                                                        {parts[0]}
                                                        <span className={`font-bold ${getHighlightColor(currentSlide)}`}>{highlightPhrase}</span>
                                                        {parts[1]}
                                                    </>
                                                );
                                            })()}
                                        </h1>
                                        <div className="font-light font-sans text-base sm:text-lg md:text-lg lg:text-xl xl:text-2xl leading-7 sm:leading-8 md:leading-8 lg:leading-7 text-white lg:text-[#2B2F31] max-w-[21rem] sm:max-w-lg mx-auto lg:mx-0" dangerouslySetInnerHTML={{ __html: slidesBanner[currentSlide].description || '' }} />
                                        {slidesBanner[currentSlide].button && slidesBanner[currentSlide].button.url && (

                                            <Link href={slidesBanner[currentSlide].button.url}>
                                                <Button
                                                    className="cursor-pointer h-14 sm:h-16 w-[80vw] max-w-[24rem] lg:w-auto lg:max-w-none min-w-[194px] sm:min-w-[200px] rounded-full lg:rounded-none text-white lg:text-[#2B2F31] text-center font-medium mx-auto lg:mx-0 text-base sm:text-lg md:text-lg lg:text-xl xl:text-2xl px-6 sm:px-8 md:px-10 bg-transparent border-[3px] sm:border-4 border-[#F15A29] hover:bg-[#F15A29] hover:text-white transition-colors duration-300"
                                                >
                                                    {slidesBanner[currentSlide].button.label}
                                                </Button>
                                            </Link>
                                        )}

                                        {/* Navegação por pontos - Mobile */}
                                        <div className="flex lg:hidden justify-center gap-2 mt-7 sm:mt-10">
                                            {slidesBanner.map((_, index) => (
                                                <button
                                                    key={index}
                                                    onClick={() => setCurrentSlide(index)}
                                                    className={`h-1.5 rounded-full transition-all duration-300 cursor-pointer ${index === currentSlide
                                                        ? 'bg-[#F15A29] scale-125 w-10 sm:w-12'
                                                        : 'bg-white/90 hover:bg-white/75 w-7 sm:w-9'
                                                        }`}
                                                    aria-label={`Ir para slide ${index + 1}`}
                                                />
                                            ))}
                                        </div>
                                    </div>


                                </div>
                            </motion.div>
                        </AnimatePresence>



                        {/* Navegação por pontos - Desktop */}
                        <div className="container p-4 sm:p-6 lg:p-8 relative h-full w-full mx-auto">
                            <div className="absolute bottom-4 sm:bottom-6 lg:bottom-18 left-1/2 -translate-x-1/2 z-40 hidden lg:flex gap-3">
                                {slidesBanner.map((_, index) => (
                                    <button
                                        key={index}
                                        onClick={() => setCurrentSlide(index)}
                                        className={`h-1.5 rounded-full transition-all duration-300 cursor-pointer ${index === currentSlide
                                            ? 'bg-[#F15A29] scale-125 w-10 sm:w-12'
                                            : 'bg-white/90 hover:bg-white/75 w-7 sm:w-9'
                                            }`}
                                        aria-label={`Ir para slide ${index + 1}`}
                                    />
                                ))}
                            </div>

                            {/* Seta para baixo no bottom do header */}
                            <BrandChevron/>
                        </div>
                    </div>
                </div>



            </div>
        </>

    );
}
